import { useEffect, useState } from 'react'
import { Avatar } from '../../components/Avatar'
import { TrophyShelf } from '../../components/TrophyShelf'
import { SectionTitle } from '../../components/SectionTitle'
import { PageHeader } from '../../components/PageHeader'
import { BackgroundTitle } from '../../components/BackgroundTitle'
import {
  fetchMyTrophies, fetchProfileStats, fetchPublicProfile,
  type ProfileStats, type PublicProfileBasics, type Trophy,
} from '../../lib/api/profile'
import { fetchMyMutedIds, muteUser, unmuteUser } from '../../lib/api/mutes'
import { useAuth } from '../../lib/auth'
import { readError } from '../../lib/errors'
import { useT } from '../../i18n'

interface Props {
  profileId: string
  onBack: () => void
  /** Open the winners screen for the week a trophy was won in. Same
   *  behaviour as the owner's own shelf; omit to render inert. */
  onOpenTrophy?: (t: Trophy) => void
}

/**
 * Public, read-only view of another user's profile. Surfaced from
 * the weekly voting + winners screens (and anywhere else we render
 * a person's name as a tap target). Shows:
 *   - Avatar + display name (+ @username if set)
 *   - Stats: games played / weekly events entered / votes received
 *   - Trophy shelf (with the same per-event art the owner sees)
 *
 * Deliberately does NOT show:
 *   - Recent activity (the kind = 'iet_play' etc. feed) — that's a
 *     personal scroll for the owner, not for randoms.
 *   - Email, subscription status, anything billing-related.
 *
 * `fetchProfileStats` and `fetchMyTrophies` already accept any
 * profile id (the function names just predate this surface). The
 * `profiles_read` RLS policy lets any authenticated user read the
 * basic columns, so no new policy work was needed.
 */
export function PublicProfileScreen({ profileId, onBack, onOpenTrophy }: Props) {
  const t = useT()
  const { profile: myProfile } = useAuth()
  const isOwnProfile = myProfile?.id === profileId
  const [profile, setProfile] = useState<PublicProfileBasics | null>(null)
  const [stats, setStats] = useState<ProfileStats | null>(null)
  const [trophies, setTrophies] = useState<Trophy[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [isMuted, setIsMuted] = useState(false)
  const [muteBusy, setMuteBusy] = useState(false)

  // Load whether I've muted this player (signed-in, not my own profile).
  useEffect(() => {
    if (isOwnProfile || !myProfile) return
    let cancelled = false
    fetchMyMutedIds()
      .then(ids => { if (!cancelled) setIsMuted(ids.includes(profileId)) })
      .catch(() => { /* non-fatal */ })
    return () => { cancelled = true }
  }, [profileId, isOwnProfile, myProfile])

  async function toggleMute() {
    if (muteBusy) return
    setMuteBusy(true)
    const next = !isMuted
    setIsMuted(next) // optimistic
    try {
      if (next) await muteUser(profileId)
      else await unmuteUser(profileId)
    } catch (e) {
      setIsMuted(!next) // revert on failure
      setError(readError(e))
    } finally {
      setMuteBusy(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const [p, s, t] = await Promise.all([
          fetchPublicProfile(profileId),
          fetchProfileStats(profileId),
          fetchMyTrophies(profileId),
        ])
        if (cancelled) return
        setProfile(p)
        setStats(s)
        setTrophies(t)
      } catch (e) {
        if (!cancelled) setError(readError(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [profileId])

  return (
    <div className="screen">
      <BackgroundTitle text="Profile" color="#D9E026" />
      <PageHeader title={t('Profile')} onBack={onBack} />

      <div className="px-5 space-y-6 relative z-10 pb-10">
        {loading && (
          <div className="card p-5 space-y-3">
            <div className="h-16 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-12 rounded-2xl bg-brand-elevated animate-pulse" />
          </div>
        )}

        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {!loading && !profile && (
          <div className="card p-5 text-center">
            <p className="font-semibold text-brand-text">{t('Profile not found')}</p>
            <p className="text-sm text-brand-muted mt-1">{t('This player may have deleted their account.')}</p>
          </div>
        )}

        {profile && (
          <>
            {/* Identity card — same shape as ProfileScreen, no Edit button. */}
            <div className="card p-5">
              <div className="flex items-center gap-4">
                <Avatar
                  name={profile.display_name}
                  avatarUrl={profile.avatar_url}
                  size="xl"
                />
                <div className="flex-1 min-w-0">
                  <h2 className="font-display text-xl font-bold text-brand-text truncate">
                    {profile.display_name}
                  </h2>
                  {profile.username && (
                    <p className="text-xs text-brand-muted mt-1">
                      @{profile.username}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-0 mt-5 pt-5 border-t border-brand-border">
                <Stat label={t('Games Played')}   value={stats?.games_played ?? null} />
                <Stat label={t('Weekly Events')}  value={stats?.weekly_events_entered ?? null} />
                <Stat label={t('Votes Received')} value={stats?.votes_received ?? null} />
              </div>

              {/* Mute: hides this player's weekly submissions for you
                  (they render as "muted"). Reversible with Unmute. */}
              {!isOwnProfile && myProfile && (
                <button
                  onClick={toggleMute}
                  disabled={muteBusy}
                  className="mt-5 w-full py-2.5 rounded-2xl border border-brand-border
                             text-sm font-semibold text-brand-text
                             active:scale-[0.98] transition-transform disabled:opacity-50"
                >
                  {isMuted
                    ? t('Unmute {name}', { name: profile.display_name })
                    : t('Mute {name}', { name: profile.display_name })}
                </button>
              )}
              {isMuted && (
                <p className="mt-2 text-[11px] text-brand-muted text-center">{t('Their weekly submissions show as “muted” for you.')}</p>
              )}
            </div>

            <div>
              <SectionTitle className="mb-3">{t('Trophy Shelf')}</SectionTitle>
              <div className="card p-4">
                {trophies === null ? (
                  <div className="h-24 rounded-sm bg-brand-elevated animate-pulse" />
                ) : (
                  <TrophyShelf trophies={trophies} onOpenTrophy={onOpenTrophy} />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="flex-1 text-center">
      <p className="font-display text-2xl font-black text-brand-text leading-none">
        {value === null ? '…' : value}
      </p>
      <p className="text-[10px] uppercase tracking-widest text-brand-muted mt-1">
        {label}
      </p>
    </div>
  )
}
